import { useEffect, useRef, useState } from 'react';
import InputFrame from '../../../atoms/InputFrame';
import { geoLocationApi } from '../../../../services/geoLocationApi';

export interface CityOption {
    id: number;
    name: string;
    ibgeCode: string;
    uf: string;
}

interface Props {
    /** Optional UF filter to disambiguate homonymous cities. */
    province?: string;
    /** Called when the user selects a city (or clears the selection). */
    onSelect: (city: CityOption | null) => void;
    disabled?: boolean;
}

/**
 * Single integrated combobox (same pattern as ProfessionAutocomplete): the user
 * types the city name, the system searches and shows the results inline in the
 * same component. Search is accent- and case-insensitive (handled by the
 * geo-location backend), so "sao joao" matches "São João".
 */
const CityPicker = ({ province, onSelect, disabled }: Props) => {
    const [inputText, setInputText] = useState('');
    const [options, setOptions] = useState<CityOption[]>([]);
    const [isOpen, setIsOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [searched, setSearched] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const latestSearchRef = useRef('');

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setIsOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const search = async (term: string) => {
        const query = term.trim();
        if (query.length < 1) { setOptions([]); return; }
        latestSearchRef.current = query;
        setSearched(true);
        setIsOpen(true);
        setIsLoading(true);
        try {
            const res: any = await geoLocationApi.getCitiesPageList({
                search: query,
                province: province || undefined,
                size: 20,
            });
            if (latestSearchRef.current !== query) return;
            const list = res?.list || res?.items || [];
            setOptions(list.map((c: any) => ({
                id: c.id,
                name: c.name,
                ibgeCode: c.ibgeCode,
                uf: c.province?.initials || c.uf || '',
            })));
        } catch {
            setOptions([]);
        } finally {
            if (latestSearchRef.current === query) setIsLoading(false);
        }
    };

    const handleInputChange = (text: string) => {
        // Only update the text while typing — the search runs on Enter.
        setInputText(text);
        onSelect(null);
    };

    const handleSelect = (city: CityOption) => {
        setInputText(`${city.name}${city.uf ? ` - ${city.uf}` : ''}`);
        onSelect(city);
        setIsOpen(false);
    };

    return (
        <InputFrame label="Buscar cidade">
            <div ref={wrapperRef} className="relative w-full">
                <input
                    type="text"
                    value={inputText}
                    disabled={disabled}
                    autoComplete="off"
                    placeholder="Digite e pressione Enter..."
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            search(inputText);
                        }
                    }}
                    onFocus={() => { if (options.length) setIsOpen(true); }}
                    className="block py-2.5 px-0 w-full text-sm bg-transparent border-0 border-b-2 border-gray-300 dark:border-slate-600 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 dark:focus:border-blue-400 peer dark:text-white disabled:opacity-50"
                />
                {isLoading && <span className="absolute right-0 top-2 text-xs text-gray-400">...</span>}

                {isOpen && searched && (
                    <ul className="absolute z-50 w-full bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                        {isLoading ? (
                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Buscando...</li>
                        ) : options.length === 0 ? (
                            <li className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">Nenhuma cidade encontrada</li>
                        ) : (
                            options.map((city) => (
                                <li
                                    key={city.id}
                                    onClick={() => handleSelect(city)}
                                    className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700 dark:text-white flex items-center justify-between"
                                >
                                    <span>{city.name}{city.uf ? ` - ${city.uf}` : ''}</span>
                                    <span className="text-[10px] text-gray-400 font-mono">{city.ibgeCode}</span>
                                </li>
                            ))
                        )}
                    </ul>
                )}
            </div>
        </InputFrame>
    );
};

export default CityPicker;
